import { ShowingProgramFilters } from '../resources/showing/showing-filters';

type GeoLocation = { latitude: number; longitude: number; distance: number } | null;

export type ShowingsListKeyParams = {
    cinemaIds: number[];
    location: GeoLocation;
    selectedDate: string;
    filters: ShowingProgramFilters;
};

export type ShowingsByContentKeyParams = {
    cinemaIds: number[];
    location: GeoLocation;
    selectedDate: string;
};

export const showingKeys = {
    all: ['showings'] as const,
    lists: () => [...showingKeys.all, 'list'] as const,
    list: (params: ShowingsListKeyParams) => [...showingKeys.lists(), params] as const,
    byContent: (contentId: number | string, params: ShowingsByContentKeyParams) =>
        [...showingKeys.all, 'by-content', contentId, params] as const,
    detail: (id: number | string) => [...showingKeys.all, 'detail', id] as const,
    tags: (showingId: number | string) => [...showingKeys.all, 'tags', showingId] as const
};

export const contentKeys = {
    all: ['contents'] as const,
    lists: () => [...contentKeys.all, 'list'] as const,
    list: (filters: QueryKeyFilters) => [...contentKeys.lists(), filters] as const,
    detail: (id: number | string) => [...contentKeys.all, 'detail', id] as const
};

export const cinemaKeys = {
    all: ['cinemas'] as const,
    favorites: (userId: number | string) => [...cinemaKeys.all, 'favorites', userId] as const
};

/**
 * Serializable filter bag embedded into a list query key (page, perPage, search, sort, locale,
 * scalar ids, …). Keep it to plain scalars or scalar arrays: React Query hashes keys structurally,
 * so passing whole entities (a `cinema`/`user` object) makes keys unstable and invalidation
 * unreliable. Always pass `cinema.id` / `user.id`, never the object.
 */
export type QueryKeyFilters = Record<
    string,
    string | number | boolean | null | undefined | ReadonlyArray<string | number>
>;

/** Optional scalar params that further specialise a movie-v2 detail (locale, cinemaId, includes). */
export type MovieV2DetailParams = {
    locale?: string;
    cinemaId?: number | string;
    include?: string;
};

export const movieV2Keys = {
    all: ['movie-v2'] as const,
    lists: () => [...movieV2Keys.all, 'list'] as const,
    list: (filters: QueryKeyFilters) => [...movieV2Keys.lists(), filters] as const,
    details: () => [...movieV2Keys.all, 'detail'] as const,
    detail: (movieId: number | string, params: MovieV2DetailParams = {}) =>
        [...movieV2Keys.details(), movieId, params] as const
};

export const cinemaConfigurationKeys = {
    all: ['cinema-configuration'] as const,
    detail: (cinemaId: number | string, configurationId?: number | string) =>
        configurationId != null
            ? ([...cinemaConfigurationKeys.all, cinemaId, configurationId] as const)
            : ([...cinemaConfigurationKeys.all, cinemaId] as const)
};

export const eventRequestKeys = {
    all: ['event-requests'] as const,
    lists: () => [...eventRequestKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.lists(), cinemaId, filters] as const,
    details: () => [...eventRequestKeys.all, 'detail'] as const,
    detail: (eventRequestId: number | string) => [...eventRequestKeys.details(), eventRequestId] as const,
    count: (cinemaId?: number | string) =>
        cinemaId != null
            ? ([...eventRequestKeys.all, 'count', cinemaId] as const)
            : ([...eventRequestKeys.all, 'count'] as const),
    notes: (eventRequestId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.detail(eventRequestId), 'notes', filters] as const,
    dates: (eventRequestId: number | string) => [...eventRequestKeys.detail(eventRequestId), 'dates'] as const
};

export const cinemaProductKeys = {
    all: ['cinema-products'] as const,
    lists: () => [...cinemaProductKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...cinemaProductKeys.lists(), cinemaId, filters] as const,
    details: () => [...cinemaProductKeys.all, 'detail'] as const,
    detail: (productId: number | string) => [...cinemaProductKeys.details(), productId] as const
};

export const dealKeys = {
    all: ['deals'] as const,
    lists: () => [...dealKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...dealKeys.lists(), cinemaId, filters] as const,
    details: () => [...dealKeys.all, 'detail'] as const,
    detail: (dealId: number | string) => [...dealKeys.details(), dealId] as const
};

export const flyerTemplateKeys = {
    all: ['flyer-templates'] as const,
    lists: () => [...flyerTemplateKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...flyerTemplateKeys.lists(), cinemaId, filters] as const,
    details: () => [...flyerTemplateKeys.all, 'detail'] as const,
    detail: (flyerTemplateId: number | string) => [...flyerTemplateKeys.details(), flyerTemplateId] as const
};

export const flyerKeys = {
    all: ['flyers'] as const,
    lists: () => [...flyerKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...flyerKeys.lists(), cinemaId, filters] as const,
    details: () => [...flyerKeys.all, 'detail'] as const,
    detail: (flyerId: number | string) => [...flyerKeys.details(), flyerId] as const,
    versions: (flyerId: number | string) => [...flyerKeys.detail(flyerId), 'versions'] as const
};

export const socialMediaTemplateKeys = {
    all: ['social-media-templates'] as const,
    lists: () => [...socialMediaTemplateKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...socialMediaTemplateKeys.lists(), cinemaId, filters] as const,
    details: () => [...socialMediaTemplateKeys.all, 'detail'] as const,
    detail: (socialMediaTemplateId: number | string) =>
        [...socialMediaTemplateKeys.details(), socialMediaTemplateId] as const
};

export const newsletterKeys = {
    campaigns: {
        all: (cinemaId: number | string) => ['newsletter-campaigns', cinemaId] as const,
        list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
            ['newsletter-campaigns', cinemaId, filters] as const,
        detail: (cinemaId: number | string, campaignId: number | string) =>
            ['newsletter-campaign-detail', cinemaId, campaignId] as const,
        stats: (cinemaId: number | string) => ['newsletter-campaigns-stats', cinemaId] as const
    },
    config: (cinemaId: number | string) => ['newsletter-config', cinemaId] as const,
    sections: (cinemaId: number | string) => ['newsletter-sections', cinemaId] as const,
    subscriptionStats: (cinemaId: number | string) => ['newsletter-subscription-stats', cinemaId] as const,
    subscribers: {
        all: (cinemaId: number | string) => ['newsletter-subscribers', cinemaId] as const,
        list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
            ['newsletter-subscribers', cinemaId, filters] as const,
        detail: (cinemaId: number | string, subscriberId: number | string) =>
            ['newsletter-subscriber-detail', cinemaId, subscriberId] as const,
        subscriptions: (cinemaId: number | string, subscriberId: number | string) =>
            ['newsletter-subscriber-subscriptions', cinemaId, subscriberId] as const
    }
};
